<template>
  <el-button @click="open">{{ t('qrStyleView') }}</el-button>
  <el-dialog v-model="visible" :title="t('idCardStylePreviewTitle')" width="900px" align-center append-to-body>
    <div class="flex flex-wrap justify-center gap-2 mb-3">
      <el-button
          v-for="s in ID_CARD_STYLES" :key="s" size="small" round
          :type="s === previewStyle ? 'primary' : 'default'"
          @click="previewStyle = s"
      >{{ t(styleLabel(s)) }}</el-button>
    </div>
    <div v-loading="loading" class="flex justify-center items-center bg-gray-100 rounded-lg p-3" style="min-height: 420px">
      <img v-if="image" :src="image" alt="" class="rounded shadow bg-white" style="max-width: 100%; object-fit: contain">
    </div>
    <div ref="canvasHost" style="position: fixed; left: -10000px; top: 0"></div>
    <template #footer>
      <el-button @click="visible = false">{{ t('qrStyleClose') }}</el-button>
      <el-button type="primary" :disabled="previewStyle === selected" @click="useStyle">{{ t('qrStyleUse') }}</el-button>
    </template>
  </el-dialog>
</template>
<script setup lang="ts">
import {nextTick, ref, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import {useSettingsStore} from '@/stores/settingsStore'
import Examination from '@/models/customer/Examination'
import {ID_CARD_STYLES, renderIDCard} from '@/utils/idCardRender'

const props = defineProps<{ selected?: string }>()
const emit = defineEmits<{ (e: 'select', value: string): void }>()

const {t, locale} = useI18n({useScope: 'global'})
const settingsStore = useSettingsStore()

const visible = ref(false)
const loading = ref(false)
const image = ref('')
const previewStyle = ref<string>('classic')
const canvasHost = ref<HTMLDivElement>()
let renderToken = 0

const styleLabel = (s: string) => 'idCardStyle' + s.charAt(0).toUpperCase() + s.slice(1)

function sampleExam(): Examination {
  const now = new Date()
  const next = new Date(now.getTime() + 30 * 24 * 3600 * 1000)
  const dob = new Date(now.getFullYear() - 34, now.getMonth(), 1)
  return Examination.from({
    externalID: '124011',
    createdAt: now.toISOString(),
    nextAppointment: next.toISOString(),
    customer: {name: locale.value === 'ar' ? 'سارة أحمد' : 'Sara Ahmed', dateOfBirth: dob.toISOString()},
    examinator: {name: locale.value === 'ar' ? 'سلام صباح' : 'Salam Sabah'},
    vaWithoutGlassesOD: '6/18', vaWithGlassesOD: '6/6', iopOD: 15,
    vaWithoutGlassesOS: '6/24', vaWithGlassesOS: '6/6', iopOS: 16,
    distanceODSPH: -1.75, distanceODCYL: -0.5, distanceODAXIS: 180, distanceODIPD: 63,
    distanceOSSPH: -2, distanceOSCYL: -0.75, distanceOSAXIS: 175,
    readingAdd: true,
    readingODSPH: 1.5, readingODCYL: -0.5, readingODAXIS: 180, readingODIPD: 61,
    readingOSSPH: 1.5, readingOSCYL: -0.75, readingOSAXIS: 175
  } as any)
}

async function render() {
  const token = ++renderToken
  loading.value = true
  try {
    await nextTick()
    const host = canvasHost.value
    if (!host) return
    host.innerHTML = ''
    const div = document.createElement('div')
    host.appendChild(div)
    const stage = await renderIDCard({
      container: div,
      exam: sampleExam(),
      style: previewStyle.value,
      qrUrl: `https://exams.thecky.com/${settingsStore.getValue('viewer_client_key') || 'test'}/preview`
    })
    const dataUrl = stage.toDataURL({pixelRatio: 2})
    stage.destroy()
    if (token === renderToken) image.value = dataUrl
  } finally {
    if (token === renderToken) loading.value = false
  }
}

function open() {
  previewStyle.value = (ID_CARD_STYLES as readonly string[]).includes(props.selected || '') ? props.selected as string : 'classic'
  visible.value = true
  render()
}

function useStyle() {
  emit('select', previewStyle.value)
  visible.value = false
}

watch(previewStyle, () => { if (visible.value) render() })
</script>
